import { Store } from "vuex";
import { ActionTree } from "vuex";
import { IRootState, IRootGetters } from "@dcc/store/types";
import { buildAction, saveToGtm } from "../utils";
import { ScoreState, UserDetails } from "./types";
import { IScoreGetters } from "@dcc/store/score/getters/types";
import { sendSaveScoreRequest } from "./saveScore/saveScore";
import { GetterNames } from "./getters/names";
import { sendSaveActionPlanRequest } from "./saveActionPlan/saveActionPlan";
import { Gender } from "@dcc/types/gender";
import { DrinkFrequency } from "@dcc/types/drinkFrequency";
import { roundToNDecimalPlaces } from "@dcc/common/rounding";
import {
  gtmActionNames,
  gtmClickIds,
  gtmEventNames,
  SHOW_ME_HOW,
  SHOW_ME_TIPS
} from "@dcc/constants/gtmConstants";
import { IGtmClick } from "@dcc/types/gtmClick";

export const actionNames = {
  SAVE_SCORE: "saveScore",
  SAVE_ACTION_PLAN: "saveActivityPlan",
  TRACK_DFD_EVENT: "trackDFDEvent",
  SHOW_ME_HOW: "trackShowMeHow",
  SHOW_ME_HOW_CLICKED: "trackShowMeHowClicked",
  SHOW_ME_TIPS_CLICKED: "trackShowMeTipsClicked",
  TRACK_ACTIVITIES: "trackSelectedActivities"
};

export const actions: ActionTree<ScoreState, IRootState> = {
  [actionNames.SAVE_SCORE]({
    commit,
    state,
    rootState,
    rootGetters,
    getters
  }: {
    commit: any;
    state: ScoreState;
    getters: IScoreGetters;
    rootState: IRootState;
    rootGetters: IRootGetters;
  }) {
    const drinkFrequency = rootGetters["drinking/drinkFrequency"];
    const bingeFrequency = rootGetters["drinking/bingeFrequency"];
    const riskLevel = getters["riskLevel"];

    const weeklyUnitsAndCalories = getters["weeklyUnitsAndCalories"];
    const dailyUnitsAndCalories = getters["dailyUnitsAndCalories"];
    const percentWhoDrinkLess = getters["percentWhoDrinkLess"];
    const gender = rootGetters["profile/gender"];
    const age = rootGetters["profile/age"];

    const drinksForEachWeekday = rootGetters["drinking/drinksOnADay"];
    const actualDrinksWithoutDays = rootGetters["drinking/actualDrinkingDays"];

    const amountAboveLowRisk = getters["amountAboveLowRisk"];
    const auditCScore = getters["auditCScore"];
    const totalWeeklyDrinks = getters["totalWeeklyDrinks"];
    const drinkingDaysPerMonth = rootGetters["drinking/monthlyDrinkingDays"];

    const postcode = rootGetters["profile/postcode"];

    const clientId = rootState.clientId;

    if (
      bingeFrequency !== undefined &&
      drinkFrequency !== undefined &&
      gender !== undefined
    ) {
      const scoreData = {
        age,
        clientId,
        drinkFrequency,
        bingeFrequency,
        riskLevel,
        dailyUnitsAndCalories,
        weeklyUnitsAndCalories,
        percentWhoDrinkLess,
        gender,
        drinksForEachWeekday,
        actualDrinksWithoutDays,
        amountAboveLowRisk,
        auditCScore,
        totalWeeklyDrinks,
        drinkingDaysPerMonth,
        postcode
      };

      saveToGtm({
        state: rootState,
        eventName: gtmEventNames.SAVE_SCORE,
        action: gtmActionNames.RESULTS,
        request: scoreData
      });
      sendSaveScoreRequest(scoreData, rootState.accessToken);
    }
  },
  [actionNames.SHOW_ME_HOW_CLICKED]({ rootState }: { rootState: IRootState }) {
    const request: IGtmClick = {
      click_id: gtmClickIds.SHOW_ME_HOW,
      label: SHOW_ME_HOW
    };
    saveToGtm({
      state: rootState,
      eventName: gtmEventNames.CLICK,
      action: gtmEventNames.CLICK,
      request
    });
  },
  [actionNames.SHOW_ME_TIPS_CLICKED]({ rootState }: { rootState: IRootState }) {
    const request: IGtmClick = {
      click_id: gtmClickIds.SHOW_ME_TIPS,
      label: SHOW_ME_TIPS
    };
    saveToGtm({
      state: rootState,
      eventName: gtmEventNames.CLICK,
      action: gtmEventNames.CLICK,
      request
    });
  },
  [actionNames.TRACK_ACTIVITIES](
    {
      commit,
      state,
      rootState,
      rootGetters,
      getters
    }: {
      commit: any;
      state: ScoreState;
      getters: IScoreGetters;
      rootState: IRootState;
      rootGetters: IRootGetters;
    },
    details: UserDetails
  ) {
    rootGetters["activity/GetSelectedActivities"]
      .map((activity) => activity.title)
      .forEach((activity) => {
        saveToGtm({
          state: rootState,
          eventName: gtmEventNames.TRACK_ACTIVITIES,
          request: {
            activity
          },
          action: gtmActionNames.RESULTS
        });
      });
  },
  [actionNames.TRACK_DFD_EVENT]({
    commit,
    state,
    rootState,
    rootGetters,
    getters
  }: {
    commit: any;
    state: ScoreState;
    getters: IScoreGetters;
    rootState: IRootState;
    rootGetters: IRootGetters;
  }) {
    const drinkFrequency = rootGetters["drinking/drinkFrequency"];
    const gender = rootGetters["profile/gender"];
    const age = rootGetters["profile/age"];
    const drinksTotalPerWeek = getters[GetterNames.WEEKLYDRINKS];
    const drinkerType = getters[GetterNames.DRINKERTYPE];
    const clientId = rootState.clientId;
    const unitsAndCalories = getters[GetterNames.WEEKLYUNITSANDCALORIES];
    const maxUnits = getters[GetterNames.HIGHESTUNITSPERDAY];
    const dfdData = {
      age,
      clientId,
      frequency:
        drinkFrequency == undefined ? "" : DrinkFrequency[drinkFrequency], // Convert to string
      drinker_type: drinkerType.drinkerType,
      beer_bottle: drinksTotalPerWeek.beer,
      beer_pint: drinksTotalPerWeek.pint,
      wine: drinksTotalPerWeek.wine,
      spirit: drinksTotalPerWeek.spirit,
      gender: gender == undefined ? "" : Gender[gender], // Convert to string
      units: roundToNDecimalPlaces(unitsAndCalories.units, 2),
      single_day_unit: roundToNDecimalPlaces(maxUnits, 2)
    };

    saveToGtm({
      state: rootState,
      eventName: gtmEventNames.TRACK_DFD_EVENT,
      request: dfdData,
      action: gtmActionNames.RESULTS
    });
  },
  [actionNames.SHOW_ME_HOW]({
    commit,
    state,
    rootState,
    rootGetters,
    getters
  }: {
    commit: any;
    state: ScoreState;
    getters: IScoreGetters;
    rootState: IRootState;
    rootGetters: IRootGetters;
  }) {
    const drinkingDays = rootGetters["drinking/originalDrinkingDays"].length;
    const drinkFreeDays = rootGetters["drinking/selectedDrinkFreeDays"].length;
    const targetPercentage = rootGetters["drinking/targetPercentage"];
    const initialPercentage = getters[GetterNames.ORIGINALPERCENTWHODRINKLESS];
    const originalUnitsAndCaloriesPerWeek =
      getters[GetterNames.ORIGINALWEEKLYUNITSANDCALORIES];
    const unitsAndCaloriesPerWeek = getters[GetterNames.WEEKLYUNITSANDCALORIES];

    const dfdData = {
      unitAverageReduction:
        unitsAndCaloriesPerWeek != undefined
          ? roundToNDecimalPlaces(
              originalUnitsAndCaloriesPerWeek.units -
                unitsAndCaloriesPerWeek.units,
              1
            )
          : 0,
      totalDrinkDays: drinkingDays,
      totalDrinkFreeDays: drinkFreeDays,
      cutdownGoal: targetPercentage,
      cutdownPledge:
        targetPercentage != undefined ? initialPercentage - targetPercentage : 0
    };

    saveToGtm({
      state: rootState,
      eventName: gtmEventNames.SHOW_ME_HOW,
      request: dfdData,
      action: gtmActionNames.RESULTS
    });
  },
  [actionNames.SAVE_ACTION_PLAN](
    {
      commit,
      state,
      rootState,
      rootGetters,
      getters
    }: {
      commit: any;
      state: ScoreState;
      getters: IScoreGetters;
      rootState: IRootState;
      rootGetters: IRootGetters;
    },
    details: UserDetails
  ) {
    const age = rootGetters["profile/age"];
    const gender = rootGetters["profile/gender"];
    const originalUnitsAndCaloriesPerWeek =
      getters[GetterNames.ORIGINALWEEKLYUNITSANDCALORIES];
    const unitsAndCaloriesPerWeek = getters[GetterNames.WEEKLYUNITSANDCALORIES];
    const tips = rootGetters["activity/GetSelectedIds"];
    const percentage =
      rootGetters["drinking/targetPercentage"] ||
      getters[GetterNames.PERCENTWHODRINKLESS];
    const droppedDays = rootGetters["drinking/selectedDrinkFreeDays"].length;
    const numDrinkingDays = rootGetters["drinking/drinksOnADay"]
      ? rootGetters["drinking/drinksOnADay"].length
      : 0;

    const selectedActivities = rootGetters[
      "activity/GetSelectedActivities"
    ].map((activity) => activity.title);

    const clientId = rootState.clientId;

    if (gender !== undefined) {
      const planData = {
        ...details,
        age,
        gender,
        originalUnitsAndCaloriesPerWeek,
        unitsAndCaloriesPerWeek,
        droppedDays,
        tips,
        percentage,
        clientId,
        numDrinkingDays,
        selectedActivities
      };

      saveToGtm({
        state: rootState,
        eventName: gtmEventNames.SAVE_ACTION_PLAN,
        request: planData,
        action: gtmActionNames.RESULTS
      });
      sendSaveActionPlanRequest(planData);
    }
  }
};

export const getActions = (store: Store<IRootState>) => ({
  saveScore: buildAction<void>(store, "score", actionNames.SAVE_SCORE),
  saveActionPlan: buildAction<UserDetails>(
    store,
    "score",
    actionNames.SAVE_ACTION_PLAN
  ),
  trackDFDEvent: buildAction<void>(store, "score", actionNames.TRACK_DFD_EVENT),
  trackShowMeHow: buildAction<void>(store, "score", actionNames.SHOW_ME_HOW),
  trackShowMeHowClicked: buildAction<void>(
    store,
    "score",
    actionNames.SHOW_ME_HOW_CLICKED
  ),
  trackShowMeTipsClicked: buildAction<void>(
    store,
    "score",
    actionNames.SHOW_ME_TIPS_CLICKED
  ),

  trackSelectedActivities: buildAction<void>(
    store,
    "score",
    actionNames.TRACK_ACTIVITIES
  )
});
